<template>
  <div v-if="show" :class="positionClass">
    <div
      class="relative flex items-center justify-center"
      :class="[position === 'fixed' ? 'mb-14' : '', !hideOverlay ? 'z-30' : 'z-0']"
    >
      <div
        class="absolute flex flex-col items-center justify-center font-inter text-text-secondary"
        :class="size === 'large' ? 'top-[60px]' : 'top-[40px]'"
      >
        <div class="flex items-center justify-center gap-4 text-always-light">
          <div>...</div>
          <IconPhxCapLogo
            class="animate-scale-pulse"
            :class="[
              size === 'large' ? 'h-16 w-16' : 'h-10 w-10',
              hideOverlay ? 'fill-strongest' : 'fill-always-light',
            ]"
          />

          <div>...</div>
        </div>
        <p
          class="text-center font-inter"
          :class="{
            'text-xs': smallText,
            'mt-3': size === 'large',
            'text-strongest': hideOverlay,
            'text-always-light': !hideOverlay,
          }"
        >
          {{ currentMessage }}
        </p>
      </div>
      <div
        class="relative flex items-center justify-center"
        :class="size === 'large' ? 'h-54 w-54' : 'h-36 w-36'"
      >
        <div
          class="animate-rotating-border rounded-full border-[4px] border-outline-dividers opacity-20"
          :class="size === 'large' ? 'h-54 w-54' : 'h-36 w-36'"
        ></div>
        <div
          class="absolute right-0 top-0 animate-custom-spin rounded-full border-[4px] border-transparent border-t-outline-dividers opacity-30"
          :class="size === 'large' ? 'h-54 w-54' : 'h-36 w-36'"
        ></div>
      </div>
    </div>
    <div
      v-if="!hideOverlay"
      id="loading-spinner-overlay"
      class="fixed inset-0 z-10 h-full w-full backdrop-blur-[2px] bg-[rgba(20,17,11,0.8)]"
    />
  </div>
</template>

<script setup>
import { watch, computed, ref, onUnmounted } from "vue";

import { IconPhxCapLogo } from "@/components/icons";

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  hideOverlay: {
    type: Boolean,
    default: false,
  },
  position: {
    type: String,
    default: "fixed",
  },
  message: {
    type: String,
    default: "Loading",
  },
  smallText: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: "large", // 'normal' or 'large'
    validator: (value) => ["normal", "large"].includes(value),
  },
  progressiveMessages: {
    type: Array,
    default: () => [],
  },
  messageInterval: {
    type: Number,
    default: 3000,
  },
});

const currentMessage = ref(props.message);
const messageIndex = ref(0);
let messageTimer = null;

const positionClass = computed(() => {
  return {
    "fixed inset-0 z-20 flex h-full w-full items-center justify-center": props.position === "fixed",
    "relative z-50 flex items-center justify-center": props.position === "relative",
  };
});

function startProgressiveLoading() {
  if (props.progressiveMessages.length === 0) {
    currentMessage.value = props.message;
    return;
  }

  // Start with first message
  currentMessage.value = props.progressiveMessages[0];
  messageIndex.value = 0;

  // If there's only one message, don't start interval
  if (props.progressiveMessages.length === 1) {
    return;
  }

  // Cycle through messages but stop at the last one
  messageTimer = setInterval(() => {
    messageIndex.value++;

    // If we've reached the last message, stop there
    if (messageIndex.value >= props.progressiveMessages.length) {
      messageIndex.value = props.progressiveMessages.length - 1;
      currentMessage.value = props.progressiveMessages[messageIndex.value];

      // Stop the timer permanently
      clearInterval(messageTimer);
      messageTimer = null;
      return;
    }

    currentMessage.value = props.progressiveMessages[messageIndex.value];
  }, props.messageInterval);
}

function stopProgressiveLoading() {
  if (messageTimer) {
    clearInterval(messageTimer);
    messageTimer = null;
  }
  currentMessage.value = props.message;
  messageIndex.value = 0;
}

watch(
  () => props.show,
  (newShow) => {
    if (newShow) {
      document.body.style.overflow = "hidden";
      startProgressiveLoading();
    } else {
      document.body.style.overflow = "auto";
      stopProgressiveLoading();
    }
  }
);

onUnmounted(() => {
  stopProgressiveLoading();
  document.body.style.overflow = "auto";
});
</script>
